import { logError } from 'packages/lib/utils/logger';

interface PCDHeader {
  str: string;
  version?: number;
  fields: string[];
  size: number[];
  sizeMap: Record<string, number>;
  type?: Record<string, string>;
  count?: number[];
  width?: number;
  height?: number;
  viewpoint?: string;
  points: number;
  data: unknown;
  headerLen: number;
  rowSize: number;
  offset: Record<string, number>;
}

export function parseHeader(data: string): PCDHeader {
  // TODO properly throw when one of these is not found
  const pcdHeader: PCDHeader = {
    str: '',
    headerLen: 0,
    data: null,
    fields: [],
    offset: {},
    rowSize: 0,
    size: [],
    points: 0,
    sizeMap: {},
  };
  const result1 = data.search(/[\r\n]DATA\s(\S*)\s/i);
  const result2 = /[\r\n]DATA\s(\S*)\s/i.exec(data.substr(result1 - 1));

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  [, pcdHeader.data] = result2!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  pcdHeader.headerLen = result2![0].length + result1;
  pcdHeader.str = data.substr(0, pcdHeader.headerLen);

  // remove comments

  pcdHeader.str = pcdHeader.str.replace(/#.*/gi, '');

  // parse

  const version = /VERSION (.*)/i.exec(pcdHeader.str);
  const fields = /FIELDS (.*)/i.exec(pcdHeader.str);
  const size = /SIZE (.*)/i.exec(pcdHeader.str);
  const type = /TYPE (.*)/i.exec(pcdHeader.str);
  const count = /COUNT (.*)/i.exec(pcdHeader.str);
  const width = /WIDTH (.*)/i.exec(pcdHeader.str);
  const height = /HEIGHT (.*)/i.exec(pcdHeader.str);
  const viewpoint = /VIEWPOINT (.*)/i.exec(pcdHeader.str);
  const points = /POINTS (.*)/i.exec(pcdHeader.str);

  // evaluate

  const pcdVersionHeader = validatePcdHeaderField(version, pcdHeader.str);
  if (pcdVersionHeader) {
    pcdHeader.version = parseFloat(pcdVersionHeader);
  }

  const pcdFieldsHeader = validatePcdHeaderField(fields, pcdHeader.str);
  if (pcdFieldsHeader) {
    pcdHeader.fields = pcdFieldsHeader.split(' ');
  }

  const pcdTypeHeader = validatePcdHeaderField(type, pcdHeader.str);
  if (pcdTypeHeader) {
    pcdHeader.type = pcdTypeHeader.split(' ').reduce((acc, type, idx) => {
      const key = pcdHeader.fields[idx];
      acc[key] = type;
      return acc;
    }, {} as Record<string, string>);
  }

  const pcdWidthHeader = validatePcdHeaderField(width, pcdHeader.str);
  if (pcdWidthHeader) {
    pcdHeader.width = parseInt(pcdWidthHeader);
  }

  const pcdHeightHeader = validatePcdHeaderField(height, pcdHeader.str);
  if (pcdHeightHeader) {
    pcdHeader.height = parseInt(pcdHeightHeader);
  }

  const pcdViewpointHeader = validatePcdHeaderField(viewpoint, pcdHeader.str);
  if (pcdViewpointHeader) {
    pcdHeader.viewpoint = pcdViewpointHeader;
  }

  const pcdPointsHeader = validatePcdHeaderField(points, pcdHeader.str);
  if (pcdPointsHeader) {
    pcdHeader.points = parseInt(pcdPointsHeader, 10);
  } else {
    if (pcdHeader.width && pcdHeader.height) {
      pcdHeader.points = pcdHeader.width * pcdHeader.height;
    }
  }

  const pcdSizeHeader = validatePcdHeaderField(size, pcdHeader.str);
  if (pcdSizeHeader) {
    pcdHeader.size = pcdSizeHeader.split(' ').map(function (x) {
      return parseInt(x, 10);
    });
    pcdHeader.sizeMap = pcdHeader.size.reduce((acc, size, idx) => {
      const key = pcdHeader.fields[idx];
      acc[key] = size;
      return acc;
    }, {} as Record<string, number>);
  }

  const pcdCountHeader = validatePcdHeaderField(count, pcdHeader.str);
  if (pcdCountHeader) {
    pcdHeader.count = pcdCountHeader.split(' ').map(function (x) {
      return parseInt(x, 10);
    });
  } else {
    pcdHeader.count = [];

    for (let i = 0, l = pcdHeader.fields.length; i < l; i++) {
      pcdHeader.count.push(1);
    }
  }

  pcdHeader.offset = {};

  let sizeSum = 0;

  for (let i = 0, l = pcdHeader.fields.length; i < l; i++) {
    if (pcdHeader.data === 'ascii') {
      pcdHeader.offset[pcdHeader.fields[i]] = i;
    } else {
      pcdHeader.offset[pcdHeader.fields[i]] = sizeSum;
      sizeSum += pcdHeader.size[i] * pcdHeader.count[i];
    }
  }

  // for binary only

  pcdHeader.rowSize = sizeSum;

  return pcdHeader;
}

function validatePcdHeaderField(
  headerFieldRegex: RegExpExecArray | null,
  pcdHeaderStr: string
) {
  if (headerFieldRegex === null) {
    logError(
      `PCD Header is missing a field. Header string value: ${pcdHeaderStr}`
    );
    return null;
  }
  const [, pcdHeaderField] = headerFieldRegex;
  if (!pcdHeaderField) {
    const err = new Error(
      `PCD Header is missing SIZE field. Header Value: ${pcdHeaderStr}}`
    );
    logError(err);
    throw err;
  }
  return pcdHeaderField;
}
